import { ComponentPropsWithoutRef, Ref, forwardRef } from "react";
import styles from "./LinkButton.module.scss";
import linkStyles from "../Link/internal/Link.module.scss";
import { useTrackingId } from "../../hooks/useTrackingId";
import { DataTrackingId } from "../../types/DataTrackingId";
import cx from "classnames";
import { LayoutUtilProps } from "../../types";
import { childrenToString } from "../../internal/functions";
import { useLayoutPropsUtil } from "../../internal/hooks";

/**
 * Props for the Link component.
 * @extends CoreLinkButtonProps
 * @extends DataTrackingId
 */
export type LinkButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "type" | "disabled"
> &
  LayoutUtilProps &
  (
    | {
        /**
         * The visual appearance of the LinkButton.
         * @default "primary"
         */
        appearance?: "primary";
        /**
         * Whether the LinkButton should have a quieter, less prominent appearance.
         * @remarks Only available when appearance is "primary".
         */
        ghost?: boolean;
      }
    | {
        /**
         * The visual appearance of the LinkButton. Secondary appearance cannot be ghost.
         */
        appearance: "secondary";
        /**
         * Whether the LinkButton should have a quieter, less prominent appearance.
         * @remarks Must be false when appearance is "secondary".
         */
        ghost?: false;
      }
  ) &
  DataTrackingId;

export const LinkButton = forwardRef(
  (props: LinkButtonProps, ref: Ref<HTMLButtonElement>) => {
    const data = {
      appearance: props.appearance,
      ghost: props.ghost,
      children: childrenToString(props.children),
    };

    const trackingId = useTrackingId({
      name: "Link",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      appearance = "primary",
      ghost,
      style,
      ...rest
    } = componentProps;

    const linkClassNames = cx(
      [linkStyles["link"]],
      styles["link-button"],
      className,
      {
        [linkStyles["ghost"]]: ghost,
        [linkStyles["secondary"]]: appearance === "secondary",
      },
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <button
        className={linkClassNames}
        data-anv="link-button"
        ref={ref}
        style={styleCombined}
        data-tracking-id={trackingId}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

LinkButton.displayName = "LinkButton";
